import {
  Text,
  IconArrowLeft,
  Button,
  ErrorMessage,
  PageTitle,
  Panel,
  GreyGrey60,
  GreyGrey80,
  Primary14ExtendedRegular,
  Primary20SemiBold,
  StandardLayout,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import React, { FC } from 'react'
import { useNavigate } from 'react-router-dom'

interface SettingsPageLayoutProps {
  header: string
  // Renders in place of the default title text when provided. `header` is still
  // used for the browser tab title, so pass a plain string there too.
  headerContent?: React.ReactNode
  subheader?: React.ReactNode
  actions?: React.ReactNode
  backButton?: {
    href: string
    label: string
  }
  children: React.ReactNode
  isError?: boolean
  width?: string
  // Rendered as a right-hand column inside the page panel, divided by a
  // border — so the inspector floats within the layout rather than beside it.
  inspector?: React.ReactNode
}

export const SettingsPageLayout: FC<SettingsPageLayoutProps> = ({
  subheader,
  header,
  headerContent,
  actions,
  children,
  isError,
  backButton,
  width = '600px',
  inspector
}) => {
  const navigate = useNavigate()

  return (
    <Panel width="100%" height="100%" display="flex" flexDirection="column">
      {/* Settings pages have no header bar of their own, so this mobile-only
          row is where the shared nav burger lands. */}
      <Box display={{ base: 'block', md: 'none' }} flexShrink={0}>
        <StandardLayout.Header />
      </Box>
      <Box
        flex="1"
        minHeight="0"
        display="grid"
        gridTemplateColumns={inspector ? '1fr auto' : '1fr'}
        // Bound the row to the panel height so the content column scrolls
        // internally rather than overflowing the (overflow:hidden) panel.
        gridTemplateRows="minmax(0, 1fr)"
      >
        <Box
          minHeight="0"
          overflow="auto"
          paddingTop="40px"
          paddingBottom="120px"
        >
          <PageTitle title={[header]} />
          <Flex
            flexDirection="column"
            width={width}
            // The fixed column is wider than a phone screen, so cap it.
            maxWidth="100%"
            margin="auto"
            gap="32px"
            data-testid="settings.pageContent"
          >
            <Flex justifyContent="space-between">
              <Flex flexDirection="column" width="100%">
                {backButton && (
                  <Button
                    leadingIcon={<IconArrowLeft />}
                    variant="ghost"
                    color={GreyGrey60}
                    style={{
                      width: 'fit-content',
                      marginBottom: '8px'
                    }}
                    onClick={() => {
                      navigate(backButton.href)
                    }}
                  >
                    {backButton.label}
                  </Button>
                )}
                {headerContent ?? (
                  <Text {...Primary20SemiBold} color={GreyGrey80} minH="24px">
                    {header}
                  </Text>
                )}
                {typeof subheader === 'string' ? (
                  <Text
                    marginTop="8px"
                    {...Primary14ExtendedRegular}
                    color={GreyGrey80}
                  >
                    {subheader}
                  </Text>
                ) : (
                  subheader
                )}
              </Flex>
              {actions}
            </Flex>
            {isError ? (
              <Center paddingY="10rem">
                <ErrorMessage />
              </Center>
            ) : (
              children
            )}
          </Flex>
        </Box>
        {/* The Inspector draws its own left border, so no divider is needed here. */}
        {inspector && (
          <Box minHeight="0" overflow="auto">
            {inspector}
          </Box>
        )}
      </Box>
    </Panel>
  )
}
